import React from "react";
import NextLink from "next/link";
import PropTypes from "prop-types";
import {
  Box,
  Drawer,
  useMediaQuery,
  ListItem,
  Collapse,
} from "@mui/material";
import Menuitems from "./MenuItems";
import { useRouter } from "next/router";
import { useSessionStorageState } from '../../../utils/useSessionStorageState'
import { initialState, lookupStorageKey } from '../../../apollo/cache'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

const Sidebar = ({
  isMobileSidebarOpen,
  onSidebarClose,
  isSidebarOpen,
  onCollapsedChange,
}) => {
  const [state, setStorageState] = useSessionStorageState(
    lookupStorageKey,
    initialState
  );

  const [open, setOpen] = React.useState(null);

  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const [isCollapsed, setIsCollapsed] = React.useState(lgUp);

  React.useEffect(() => {
    // Mobile: always expanded. Desktop: collapsed by default, expands on hover.
    setIsCollapsed(lgUp);
  }, [lgUp]);

  const navy = "#08364f";
  const hoverBgColor = "#405B70";
  const TRANSITION_EASING = "cubic-bezier(0.2, 0.8, 0.2, 1)";
  const ICON_BOX_SIZE_PX = 37;
  const ICON_SIZE_PX = 22;
  const ICON_GAP_PX = 8;
  // Align nav labels with logo title: logo uses pl 18 + img ml 5 + img 28 + gap 8 → text at 59px; row pl  + icon column 37 → need +4 after icon.
  const ROW_INNER_PADDING_LEFT_PX = 18;
  const LOGO_IMG_MARGIN_LEFT_PX = 5;
  const LOGO_IMG_SIZE_PX = 28;
  const LOGO_TEXT_GAP_PX = 8;
  const TAB_ICON_TRAILING_MR_PX =
    ROW_INNER_PADDING_LEFT_PX +
      LOGO_IMG_MARGIN_LEFT_PX +
      LOGO_IMG_SIZE_PX +
      LOGO_TEXT_GAP_PX -
      ROW_INNER_PADDING_LEFT_PX -
      ICON_BOX_SIZE_PX;
  const CHILD_LABEL_SPACER_W_PX = ICON_BOX_SIZE_PX + TAB_ICON_TRAILING_MR_PX;
  const SIDEBAR_EXPANDED_WIDTH_PX = 265;
  const SIDEBAR_COLLAPSED_WIDTH_PX = ICON_BOX_SIZE_PX + 32; // Fits `p={2}` inside the icon box.

  const collapseTimeoutRef = React.useRef(null);
  React.useEffect(() => {
    return () => {
      if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
    };
  }, []);

  const handleMouseEnterSidebar = () => {
    if (!lgUp) return;
    if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
    collapseTimeoutRef.current = null;
    setIsCollapsed(false);
  };

  const handleMouseLeaveSidebar = () => {
    if (!lgUp) return;
    if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
    // Delay collapse a bit so the width transition doesn't immediately trigger a "leave".
    collapseTimeoutRef.current = setTimeout(() => setIsCollapsed(true), 140);
  };

  React.useEffect(() => {
    onCollapsedChange?.(isCollapsed);
  }, [isCollapsed, onCollapsedChange]);

  let curl = useRouter();
  const location = curl.pathname;

  const handleClick = (index) => {
    setOpen((prevOpen) => (prevOpen === index ? null : index));
  };

  // Function to check if any child of the current menu item is active
  const isChildActive = (children) => {
    return children.some((child) => location.includes(child.href));
  };

  // Check if user has any permission in a section
  const hasAnyPermission = (permissionObject) => {
    if (!permissionObject) return false;
    return Object.values(permissionObject).some(value => value === true);
  };

  // Filter menu items based on user permissions
  const getFilteredMenuItems = () => {
    if (!state?.user) return Menuitems;

    return Menuitems.filter((item) => {
      // Always show logout
      if (item.href === '/logout') return true;

      // Customer Service
      if (item.href === '/customer-service') {
        return hasAnyPermission(state.user.customer_service);
      }

      // TikTok (same permissions as Customer Service)
      if (item.href === '/tiktok') {
        return hasAnyPermission(state.user.customer_service);
      }

      // Graphic Design (orders, mockups, history)
      if (item.title === 'Graphic Design') {
        return hasAnyPermission(state.user.graphic_design);
      }

      // Return Requests
      if (item.href === '/requests/listing') {
        return hasAnyPermission(state.user.refund_requests);
      }

      // Settings (add_business_days, add_rush_days, add_wholesale_days)
      if (item.title === 'Turnaround Time') {
        return Boolean(
          state.user.accounts?.add_business_days ||
          state.user.accounts?.add_rush_days ||
          state.user.accounts?.add_wholesale_days
        );
      }

      // Accounts (view_accounts or manage_roles)
      if (item.title === 'Accounts') {
        const hasAccountsPermission = state.user.accounts?.view_accounts || state.user.accounts?.manage_roles;
        if (!hasAccountsPermission) return false;

        // Filter children
        if (item.children) {
          item.children = item.children.filter(child => {
            if (child.href === '/accounts') {
              return state.user.accounts?.view_accounts;
            }
            if (child.href === '/roles') {
              return state.user.accounts?.manage_roles;
            }
            return true;
          });
        }
        return item.children && item.children.length > 0;
      }

      // Default: show the item
      return true;
    }).map(item => ({...item})); // Deep copy to avoid mutating original
  };

  const filteredMenuItems = getFilteredMenuItems();

  const SidebarContent = (
    <Box
      height="100%"
      className="sidebar"
      sx={{
        px: 2,
        pt: 0,
        pb: 2,
      }}
    >
      <Box
        className="logo"
        sx={{
          height: "64px",
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "flex-start",
        }}
      >
        <NextLink href="/">
          <Box
            className="logo-wrap"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              py: 0,
              padding: 0,
              textDecoration: "none",
              width: isCollapsed ? "100%" : "calc(100% - 24px)",
              mx: isCollapsed ? 0 : "12px",
              pl: isCollapsed ? 0 : "18px",
              justifyContent: isCollapsed ? "center" : "flex-start",
            }}
          >
            <Box
              component="img"
              src="/logo_mini.png"
              alt="Universal Admin Portal"
              sx={{
                width: "37px",
                height: "37px",
                borderRadius: "999px",
                flexShrink: 0
              }}
            />
            {!isCollapsed && (
              <Box
                className="logo-text"
                sx={{
                  lineHeight: "20px",
                  fontWeight: 600,
                  fontStyle: "normal",
                  fontSize: "14px",
                  color: "white",
                  whiteSpace: "nowrap",
                }}
              >
                Universal Admin Portal
              </Box>
            )}
          </Box>
        </NextLink>
      </Box>

      <Box component="nav" sx={{ display: "flex", flexDirection: "column", gap: `${ICON_GAP_PX}px` }}>
        {filteredMenuItems.map((item, index) => {
          const isItemSelected = location.includes(item.href);
          const isAnyChildSelected = item.children && isChildActive(item.children);
          const hasChildren = Boolean(item.children?.length);
          const isParentExpanded = open === index || isAnyChildSelected;
          // If a child tab is active:
          // - collapsed: highlight parent icon only
          // - expanded: highlight only the active child (parent should not be active)
          const isParentActive = isCollapsed
            ? Boolean(isItemSelected || isAnyChildSelected)
            : Boolean(isItemSelected && !isAnyChildSelected);

          const itemFg = isParentActive ? navy : "white";
          const handleParentClick = () => {
            if (hasChildren) {
              handleClick(index);
              return;
            }
            onSidebarClose?.();
          };

          return (
            <Box key={item.title}>
              {hasChildren ? (
                <ListItem
                  button
                  disableGutters
                  disableRipple
                  onClick={handleParentClick}
                  sx={{
                    p: 0,
                    m: 0,
                    mt: 0,
                    mb: 0,
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "transparent !important",
                    },
                    position: "relative",
                      marginTop: "0px !important",
                      marginBottom: "0px !important",
                    paddingTop: 0,
                    paddingBottom: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                    boxSizing: "border-box",
                    // MUI may still apply vertical padding via MuiListItem-padding.
                    "&.MuiListItem-padding": {
                      paddingTop: "0px !important",
                      paddingBottom: "0px !important",
                    },
                    ...( !isCollapsed && !isParentActive
                      ? {
                          "&:hover::before": {
                            content: '""',
                            position: "absolute",
                            left: "12px",
                            right: "12px",
                            top: 0,
                            bottom: 0,
                            borderRadius: "5px",
                            backgroundColor: hoverBgColor,
                            zIndex: 0,
                            transition: `background-color 360ms ${TRANSITION_EASING}`,
                          },
                        }
                      : {}),
                    width: "100%",
                    justifyContent: isCollapsed ? "center" : "flex-start",
                    height: `${ICON_BOX_SIZE_PX}px`,
                    minHeight: `${ICON_BOX_SIZE_PX}px`,
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      height: `${ICON_BOX_SIZE_PX}px`,
                      width: isCollapsed ? `${ICON_BOX_SIZE_PX}px` : "calc(100% - 24px)",
                      mx: isCollapsed ? 0 : "12px",
                      borderRadius: "5px",
                      backgroundColor: isParentActive ? "#fff" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: isCollapsed ? "center" : "flex-start",
                      paddingLeft: isCollapsed ? "0px" : "18px",
                      paddingRight: isCollapsed ? "0px" : "16px",
                      position: "relative",
                      zIndex: 1,
                      transition:
                        [
                          `background-color 360ms ${TRANSITION_EASING}`,
                          `width 360ms ${TRANSITION_EASING}`,
                          `padding-left 360ms ${TRANSITION_EASING}`,
                          `padding-right 360ms ${TRANSITION_EASING}`,
                        ].join(", "),
                      ...(!isCollapsed && !isParentActive
                        ? {
                            "&:hover": {
                              backgroundColor: hoverBgColor,
                              "& .sidebar-item-icon, & .sidebar-item-label": {
                                color: "white",
                              },
                            },
                          }
                        : {}),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: itemFg,
                        width: `${ICON_BOX_SIZE_PX}px`,
                        flexShrink: 0,
                        mr: isCollapsed ? 0 : `${TAB_ICON_TRAILING_MR_PX}px`,
                        transition: `color 260ms ${TRANSITION_EASING}`,
                      }}
                      className="sidebar-item-icon"
                    >
                      <FontAwesomeIcon
                        icon={item.icon}
                        style={{
                          fontSize: `${ICON_SIZE_PX}px`,
                          width: `${ICON_SIZE_PX}px`,
                          height: `${ICON_SIZE_PX}px`,
                          color: "inherit",
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        opacity: isCollapsed ? 0 : 1,
                        maxWidth: isCollapsed ? 0 : "180px",
                        transition: `opacity 260ms ${TRANSITION_EASING}, maxWidth 360ms ${TRANSITION_EASING}`,
                        color: itemFg,
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                      className="sidebar-item-label"
                    >
                      {item.title}
                    </Box>

                    {!isCollapsed && (
                      <Box
                        sx={{
                          ml: "auto",
                          width: "20px",
                          height: "20px",
                          p: 0,
                          border: "none",
                          background: "transparent",
                          color: itemFg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: `color 260ms ${TRANSITION_EASING}`,
                        }}
                        className="sidebar-item-icon"
                      >
                        <FontAwesomeIcon
                          icon={
                            isParentExpanded ? faChevronUp : faChevronDown
                          }
                          style={{
                            fontSize: "12px",
                            width: "12px",
                            height: "12px",
                            color: "inherit",
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </ListItem>
              ) : (
                <NextLink href={item.href}>
                  <ListItem
                    button
                    disableGutters
                    disableRipple
                    onClick={handleParentClick}
                    sx={{
                      p: 0,
                      m: 0,
                      mt: 0,
                      mb: 0,
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "transparent !important",
                      },
                      position: "relative",
                        marginTop: "0px !important",
                        marginBottom: "0px !important",
                      paddingTop: 0,
                      paddingBottom: 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                      boxSizing: "border-box",
                      // MUI may still apply vertical padding via MuiListItem-padding.
                      "&.MuiListItem-padding": {
                        paddingTop: "0px !important",
                        paddingBottom: "0px !important",
                      },
                      ...( !isCollapsed && !isParentActive
                        ? {
                            "&:hover::before": {
                              content: '""',
                              position: "absolute",
                              left: "12px",
                              right: "12px",
                              top: 0,
                              bottom: 0,
                              borderRadius: "5px",
                              backgroundColor: hoverBgColor,
                              zIndex: 0,
                              transition: `background-color 360ms ${TRANSITION_EASING}`,
                            },
                          }
                        : {}),
                      width: "100%",
                      justifyContent: isCollapsed ? "center" : "flex-start",
                      height: `${ICON_BOX_SIZE_PX}px`,
                      minHeight: `${ICON_BOX_SIZE_PX}px`,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        height: `${ICON_BOX_SIZE_PX}px`,
                        width: isCollapsed ? `${ICON_BOX_SIZE_PX}px` : "calc(100% - 24px)",
                        mx: isCollapsed ? 0 : "12px",
                        borderRadius: "5px",
                        backgroundColor: isParentActive ? "#fff" : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: isCollapsed ? "center" : "flex-start",
                        paddingLeft: isCollapsed ? "0px" : "18px",
                        paddingRight: isCollapsed ? "0px" : "16px",
                        position: "relative",
                        zIndex: 1,
                        transition:
                          [
                            `background-color 360ms ${TRANSITION_EASING}`,
                            `width 360ms ${TRANSITION_EASING}`,
                            `padding-left 360ms ${TRANSITION_EASING}`,
                            `padding-right 360ms ${TRANSITION_EASING}`,
                          ].join(", "),
                        ...(!isCollapsed && !isParentActive
                          ? {
                              "&:hover": {
                                backgroundColor: hoverBgColor,
                                "& .sidebar-item-icon, & .sidebar-item-label": {
                                  color: "white",
                                },
                              },
                            }
                          : {}),
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: itemFg,
                          width: `${ICON_BOX_SIZE_PX}px`,
                          flexShrink: 0,
                          mr: isCollapsed ? 0 : `${TAB_ICON_TRAILING_MR_PX}px`,
                          transition: `color 260ms ${TRANSITION_EASING}`,
                        }}
                        className="sidebar-item-icon"
                      >
                        <FontAwesomeIcon
                          icon={item.icon}
                          style={{
                            fontSize: `${ICON_SIZE_PX}px`,
                            width: `${ICON_SIZE_PX}px`,
                            height: `${ICON_SIZE_PX}px`,
                            color: "inherit",
                          }}
                        />
                      </Box>

                      <Box
                        sx={{
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          opacity: isCollapsed ? 0 : 1,
                          maxWidth: isCollapsed ? 0 : "180px",
                          transition: `opacity 260ms ${TRANSITION_EASING}, maxWidth 360ms ${TRANSITION_EASING}`,
                          color: itemFg,
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                        className="sidebar-item-label"
                      >
                        {item.title}
                      </Box>
                    </Box>
                  </ListItem>
                </NextLink>
              )}

              {/* Check if the menu item has children */}
              {item.children && (
                <Collapse in={!isCollapsed && isParentExpanded} timeout={360} unmountOnExit>
                  <Box
                    sx={{
                      mt: `${ICON_GAP_PX}px`,
                      pr: 0,
                      pl: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: `${ICON_GAP_PX}px`,
                      transition: `opacity 260ms ${TRANSITION_EASING}`,
                    }}
                  >
                    {item.children.map((child) => {
                      const isChildActive = location.includes(child.href);
                      const childFg = isChildActive ? navy : "white";

                      return (
                        <NextLink href={child.href} key={child.title}>
                          <ListItem
                            button
                            disableGutters
                            disableRipple
                            onClick={() => onSidebarClose?.()}
                            sx={{
                              p: 0,
                              m: 0,
                              mt: 0,
                              mb: 0,
                              marginTop: "0px !important",
                              marginBottom: "0px !important",
                              paddingTop: 0,
                              paddingBottom: 0,
                              paddingLeft: 0,
                              paddingRight: 0,
                              boxSizing: "border-box",
                              backgroundColor: "transparent",
                              "&:hover": {
                                backgroundColor: "transparent !important",
                              },
                              position: "relative",
                              "&.MuiListItem-padding": {
                                paddingTop: "0px !important",
                                paddingBottom: "0px !important",
                              },
                              ...( !isCollapsed && !isChildActive
                                ? {
                                    "&:hover::before": {
                                      content: '""',
                                      position: "absolute",
                                      left: "12px",
                                      right: "12px",
                                      top: 0,
                                      bottom: 0,
                                      borderRadius: "5px",
                                      backgroundColor: hoverBgColor,
                                      zIndex: 0,
                                      transition: `background-color 360ms ${TRANSITION_EASING}`,
                                    },
                                  }
                                : {}),
                              width: "100%",
                              height: `${ICON_BOX_SIZE_PX}px`,
                              minHeight: `${ICON_BOX_SIZE_PX}px`,
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                height: `${ICON_BOX_SIZE_PX}px`,
                                width: "calc(100% - 24px)",
                                mx: "12px",
                                borderRadius: "5px",
                                backgroundColor: isChildActive ? "#fff" : "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                paddingLeft: "18px",
                                paddingRight: "16px",
                                transition: `background-color 360ms ${TRANSITION_EASING}`,
                                position: "relative",
                                zIndex: 1,
                                ...(!isCollapsed && !isChildActive
                                  ? {
                                      "&:hover": {
                                        backgroundColor: hoverBgColor,
                                        "& .sidebar-item-label": {
                                          color: "white",
                                        },
                                      },
                                    }
                                  : {}),
                              }}
                            >
                              {/* Spacer matches parent icon column so label lines up with parent text */}
                              <Box
                                aria-hidden
                                sx={{
                                  width: `${CHILD_LABEL_SPACER_W_PX}px`,
                                  height: `${ICON_BOX_SIZE_PX}px`,
                                  flexShrink: 0,
                                }}
                              />

                              <Box
                                sx={{
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  maxWidth: "180px",
                                  color: childFg,
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  transition: `color 260ms ${TRANSITION_EASING}`,
                                }}
                                className="sidebar-item-label"
                              >
                                {child.title}
                              </Box>
                            </Box>
                          </ListItem>
                        </NextLink>
                      );
                    })}
                  </Box>
                </Collapse>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open={isSidebarOpen}
        variant="persistent"
        PaperProps={{
          sx: {
            width: isCollapsed ? `${SIDEBAR_COLLAPSED_WIDTH_PX}px` : `${SIDEBAR_EXPANDED_WIDTH_PX}px`,
            transition: `width 360ms ${TRANSITION_EASING}`,
            border: "0 !important",
            boxShadow: "0px 7px 30px 0px rgb(113 122 131 / 11%)",
            backgroundColor: "#08364f",
            color: "white",
          },
          onMouseEnter: handleMouseEnterSidebar,
          onMouseLeave: handleMouseLeaveSidebar,
        }}
      >
        {SidebarContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      open={isMobileSidebarOpen}
      onClose={onSidebarClose}
      PaperProps={{
        sx: {
          width: "265px",
          border: "0 !important",
          backgroundColor: "#08364f!important",
          color: "white",
        },
      }}
      variant="temporary"
    >
      {SidebarContent}
    </Drawer>
  );
};

Sidebar.propTypes = {
  isMobileSidebarOpen: PropTypes.bool,
  onSidebarClose: PropTypes.func,
  isSidebarOpen: PropTypes.bool,
  onCollapsedChange: PropTypes.func,
};

export default Sidebar;
